import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import { Heart, Repeat, Lock, ArrowRight, Sparkles, Users, HeartHandshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { DONATION_AMOUNTS } from "../data/pages";
import { PHOTOS } from "../data/photos";

const DonatePage = () => {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [step, setStep] = useState(1);
  const [donor, setDonor] = useState({ name: "", email: "" });

  const final = custom ? Number(custom) : amount;

  const onContinue = () => {
    if (!final || final < 1) {
      toast.error("Please choose a donation amount.");
      return;
    }
    setStep(2);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!donor.name || !donor.email) {
      toast.error("Please complete your details.");
      return;
    }
    toast.success(
      `Thank you, ${donor.name}! Your ${frequency === "monthly" ? "monthly" : "one-time"} gift of $${final} has been received (demo).`
    );
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Donate"
          title="Power peer-led recovery."
          description="Your gift fuels Certified Recovery Peer Specialists, training, and outreach — directly multiplying lives impacted across Central Florida."
          breadcrumbs={[{ label: "Donate" }]}
          image={PHOTOS.handsTree}
        />

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
            {/* Donation panel */}
            <div className="lg:col-span-7">
              <div className="bg-[hsl(var(--rc-paper))] border border-[hsl(var(--border))] rounded-2xl p-7 md:p-10">
                {/* Steps indicator */}
                <div className="flex items-center gap-3 text-[12px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--muted-foreground))]">
                  <span className={step >= 1 ? "text-[hsl(var(--rc-purple))]" : ""}>01 Amount</span>
                  <span className="w-6 h-px bg-[hsl(var(--border))]" />
                  <span className={step >= 2 ? "text-[hsl(var(--rc-purple))]" : ""}>02 Details</span>
                  <span className="w-6 h-px bg-[hsl(var(--border))]" />
                  <span className={step >= 3 ? "text-[hsl(var(--rc-purple))]" : ""}>03 Confirm</span>
                </div>

                {step === 1 && (
                  <div className="mt-6">
                    <h2 className="font-serif-display text-[28px] md:text-[34px] font-semibold text-[hsl(var(--rc-deep))]">
                      Choose your gift
                    </h2>
                    <div className="mt-6 inline-flex p-1 rounded-full border border-[hsl(var(--border))] bg-white">
                      {[
                        { id: "one-time", label: "One-time", icon: Heart },
                        { id: "monthly", label: "Monthly", icon: Repeat },
                      ].map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setFrequency(id)}
                          className={`px-5 h-10 rounded-full text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${
                            frequency === id
                              ? "bg-[hsl(var(--rc-deep))] text-white"
                              : "text-[hsl(var(--rc-charcoal))] hover:bg-[hsl(var(--secondary))]"
                          }`}
                        >
                          <Icon className="w-4 h-4" /> {label}
                        </button>
                      ))}
                    </div>

                    <div className="mt-7 grid grid-cols-3 gap-3">
                      {DONATION_AMOUNTS.map((a) => (
                        <button
                          key={a}
                          onClick={() => {
                            setAmount(a);
                            setCustom("");
                          }}
                          className={`h-14 rounded-xl border-2 font-serif-display text-[22px] font-semibold transition-all ${
                            amount === a && !custom
                              ? "border-[hsl(var(--rc-purple))] bg-[hsl(var(--rc-purple-soft))] text-[hsl(var(--rc-deep))]"
                              : "border-[hsl(var(--border))] bg-white text-[hsl(var(--rc-charcoal))] hover:border-[hsl(var(--rc-purple))]"
                          }`}
                        >
                          ${a}
                        </button>
                      ))}
                    </div>

                    <div className="mt-5">
                      <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">
                        Or enter a custom amount
                      </label>
                      <div className="relative max-w-xs">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] font-medium">
                          $
                        </span>
                        <Input
                          type="number"
                          min="1"
                          value={custom}
                          onChange={(e) => setCustom(e.target.value)}
                          placeholder="Custom"
                          className="h-12 pl-8 bg-white border-[hsl(var(--border))]"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={onContinue}
                      size="lg"
                      className="mt-8 bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white rounded-full h-12 px-7"
                    >
                      Continue — ${final || 0}{frequency === "monthly" ? "/mo" : ""}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {step === 2 && (
                  <form onSubmit={onSubmit} className="mt-6">
                    <h2 className="font-serif-display text-[28px] md:text-[34px] font-semibold text-[hsl(var(--rc-deep))]">
                      Your details
                    </h2>
                    <p className="mt-2 text-[14px] text-[hsl(var(--muted-foreground))]">
                      Giving ${final}{frequency === "monthly" ? " each month" : " today"} ·{" "}
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-[hsl(var(--rc-purple))] hover:underline"
                      >
                        Change
                      </button>
                    </p>
                    <div className="mt-6 grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Name</label>
                        <Input
                          value={donor.name}
                          onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                          placeholder="Your full name"
                          className="h-12 bg-white border-[hsl(var(--border))]"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Email</label>
                        <Input
                          type="email"
                          value={donor.email}
                          onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                          placeholder="you@example.com"
                          className="h-12 bg-white border-[hsl(var(--border))]"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex items-center gap-2 text-[12.5px] text-[hsl(var(--muted-foreground))]">
                      <Lock className="w-3.5 h-3.5" />
                      Demo only — no real charge will be made.
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="mt-6 bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white rounded-full h-12 px-7"
                    >
                      Complete Gift
                    </Button>
                  </form>
                )}

                {step === 3 && (
                  <div className="mt-6 text-center py-8">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(var(--rc-gold-soft))] flex items-center justify-center text-[hsl(var(--rc-gold))]">
                      <Sparkles className="w-7 h-7" />
                    </div>
                    <h2 className="font-serif-display mt-6 text-[28px] md:text-[34px] font-semibold text-[hsl(var(--rc-deep))]">
                      Thank you, {donor.name}.
                    </h2>
                    <p className="mt-3 text-[15px] text-[hsl(var(--rc-slate))] max-w-md mx-auto">
                      Your ${final}{frequency === "monthly" ? " monthly gift" : " gift"} just expanded recovery for someone in Central Florida. We’ll send a receipt to {donor.email}.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Side info */}
            <aside className="lg:col-span-5 space-y-5">
              <div className="bg-[hsl(var(--rc-deep))] text-white rounded-2xl p-7 relative overflow-hidden">
                <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[hsl(var(--rc-purple))]/40 blur-3xl" />
                <div className="relative">
                  <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
                    501(c)(3) Nonprofit
                  </p>
                  <h3 className="font-serif-display mt-3 text-[24px] font-semibold leading-snug">
                    Tax-deductible. Peer-led. 100% community-driven.
                  </h3>
                  <p className="mt-3 text-[14px] text-white/80 leading-relaxed">
                    Recovery Connections of Central Florida is a registered 501(c)(3). Your gift may be tax-deductible to the extent allowed by law.
                  </p>
                </div>
              </div>

              {[
                { icon: Users, title: "Direct community impact", body: "Your gift goes directly to peer specialists, programs and outreach across Central Florida." },
                { icon: HeartHandshake, title: "Peer-led, lived experience", body: "Most of our staff and 51% of our Board self-identify as people in recovery." },
                { icon: Lock, title: "Secure & confidential", body: "Your information is handled with the same care we bring to every relationship." },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center text-[hsl(var(--rc-purple))]">
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-[hsl(var(--rc-charcoal))]">{title}</div>
                    <p className="mt-1 text-[13px] leading-[1.55] text-[hsl(var(--muted-foreground))]">{body}</p>
                  </div>
                </div>
              ))}
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DonatePage;
